<script lang="ts">
    import ResultsPage from '@Evaluation/ValueAnalysis/Pages/ResultsPage'
    export default ResultsPage
</script>

<template>
    <div>
        <KendoGridColumnsEditorDialog :propOpened="kendoGridColumnsEditorDialogOpened"
                                      :propGrid="Grid"
                                      @close-dialog="kendoGridColumnsEditorDialogOpened = false" />

        <ScoringVotesSourceSelectorDialog :propOpened="scoringVotesSourceSelectorDialog.opened"
                                          :propDefaultSurveyId="scoringVotesSourceSelectorDialog.defaultSurveyId"
                                          :propDefaultProjectUserId="scoringVotesSourceSelectorDialog.defaultProjectUserId"
                                          :propSurveys="surveys"
                                          :propProjectUsers="projectUsers"
                                          @confirm-selection="changeScoringVotesSource"
                                          @load-panelists="loadScoringPanelists"
                                          @close-dialog="scoringVotesSourceSelectorDialog.opened = false" />

        <SaveToAttributeDialog :viewModel="saveToAttributeDialog"
                               @save-new-attribute="handleSaveResultsToNewAttributeCommand"
                               @save-existing-attribute="handleSaveResultsToExistingAttributeCommand" />

        <BarChartDialog :viewModel="barChartDialog" />

        <RadarChartDialog :viewModel="radarChartDialog" />

        <SensitivityAnalysisChartDialog :viewModel="sensitivityAnalysisChartDialog" />

        <CriterionSelectorDialog :viewModel="criterionSelectorDialog" 
                                 @confirm-selection="changeCriterion" />

        <PortfolioOrDecisionNavigation :workspaceId="workspaceId" :projectId="projectId" />

        <div class="container" style="margin-top: 80px; padding-top: 40px;">
            <h3 v-if="!rm"><i class="fa fa-spinner fa-spin"></i> Loading... </h3>

            <div v-if="rm">
                <h3>Goal: {{goalName}}</h3><br />
                <GoalNavigation :WorkspaceId="workspaceId" :ProjectId="projectId" :EvalModelId="evalModelId" ActivePage="results" :ScoringEnabled="rm.ScoringEnabled" />

                <div class="card-deck">
                    <div class="card">
                        <div class="card-body">
                            <h5 class="card-title">Ranking</h5>
                            <div class="dropdown mb-2">
                                List: <a class="text-primary" data-toggle="dropdown">{{selectedList?.Name ?? 'All'}}</a>
                                <div class="dropdown-menu" style="max-height: 250px; overflow-y: auto;">
                                    <a class="dropdown-item" @click="changeViewDef(null)">All</a>
                                    <div class="dropdown-divider"></div>
                                    <h6 class="dropdown-header">Custom lists</h6>
                                    <a class="dropdown-item" v-for="l in lists" @click="changeViewDef(l.Id)">{{l.Name}}</a>
                                </div>
                            </div>
                            <p>Rank by: <a class="text-primary" @click="showCriterionSelectorDialog">{{selectedCriterion?.Name}}</a></p>

                            <div class="dropdown d-inline-block">
                                <button class="btn btn-outline-secondary btn-sm dropdown-toggle mr-1" type="button" data-toggle="dropdown" :disabled="dataReady === false">
                                    Save to attribute
                                </button>
                                <div class="dropdown-menu">
                                    <a class="dropdown-item" @click="showScoreToAttributeSaverDialog">Value score</a>
                                    <a class="dropdown-item" @click="showRankToAttributeSaverDialog">Rank position</a>
                                </div>
                            </div>

                            <div class="dropdown d-inline-block">
                                <button class="btn btn-outline-secondary btn-sm dropdown-toggle" data-toggle="dropdown">{{precisionLabel}}</button>
                                <div class="dropdown-menu">
                                    <h6 class="dropdown-header">Precision:</h6>
                                    <a class="dropdown-item" @click="changePrecision(0)">1.0</a>
                                    <a class="dropdown-item" @click="changePrecision(1)">0.1</a>
                                    <a class="dropdown-item" @click="changePrecision(2)">0.01</a>
                                    <a class="dropdown-item" @click="changePrecision(3)">0.001</a>
                                    <a class="dropdown-item" @click="changePrecision(4)">0.0001</a>
                                    <a class="dropdown-item" @click="changePrecision(5)">0.00001</a>
                                </div>
                            </div>

                        </div>
                    </div>

                    <div class="card">
                        <div class="card-body">
                            <h5 class="card-title">Data</h5>
                            <template v-if="weightsSets.length === 0">
                                <p>Weights: <span class="text-danger">You don't have any weights sets. You can create them on the weights page.</span></p>
                            </template>
                            <template v-if="weightsSets.length > 0">
                                <div class="dropdown mb-2">
                                    Weights: <a class="text-primary dropdown-toggle" data-toggle="dropdown">{{selectedWeightsSet?.Name ?? 'None'}}</a>
                                    <div class="dropdown-menu">
                                        <a class="dropdown-item" v-for="ws in weightsSets" :key="ws.Id" @click="changeWeightsSet(ws.Id)">{{ws.Name}}</a>
                                    </div>
                                </div>
                            </template>
                            <p>Scores: <a class="text-primary" @click="showScoringVotesSourceSelectorDialog">{{selectedScoresSourceLabel}}</a></p>
                        </div>
                    </div>
                </div>

                <br />
                <h2 v-show="dataReady === false"><i class="fa fa-spinner fa-spin"></i> Loading data...</h2>
                <br />

                <div v-show="dataReady === true">
                    <div class="mb-1 d-flex justify-content-end">
                        <div class="dropdown d-inline-block mr-1">
                            <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                Compare ({{selectedAlternatives.length}})
                            </button>
                            <div class="dropdown-menu">
                                <a class="dropdown-item" @click="showBarChartDialog">Bar chart</a>
                                <a class="dropdown-item" @click="showRadarChartDialog">Radar chart</a>
                                <a class="dropdown-item" @click="showSensitivityAnalysisChartDialog">Sensitivity analysis</a>
                            </div>
                        </div>
                        <div class="dropdown d-inline-block mr-1">
                            <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                Edit columns
                            </button>
                            <div class="dropdown-menu">
                                <a class="dropdown-item" @click="showGridColumnsEditor">Custom</a>
                                <a class="dropdown-item" @click="showSubcriteriaColumns">Subcriteria</a>
                                <div class="dropdown-divider"></div>
                                <a class="dropdown-item" @click="resetGridColumns">Reset</a>
                            </div>
                        </div>
                        <button class="btn btn-outline-secondary btn-sm" @click="ExportGridCommand" title="Download to spreadsheet">
                            <i class="fa fa-download fa-lg"></i>
                        </button>
                    </div>

                    <div id="mygrid"></div>
                </div>

                <br />
                <br />

            </div>
        </div>
    </div>
</template>
